import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ChevronLeft, Save, Trash2, Plus, Image } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../lib/supabase'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import ImageUpload from '../../components/admin/ImageUpload'

const pageConfigs = {
  about: {
    name: 'About Us',
    sections: [
      { key: 'hero_title', label: 'Hero Title', type: 'text', default: 'Our Story' },
      { key: 'hero_subtitle', label: 'Hero Subtitle', type: 'textarea', default: '' },
      { key: 'story_image', label: 'Story Image', type: 'image' },
      { key: 'story_title', label: 'Story Section Title', type: 'text', default: 'Where it all began' },
      { key: 'story_content', label: 'Story Content', type: 'textarea', default: '' },
      { key: 'mission_title', label: 'Mission Title', type: 'text', default: 'Our Mission' },
      { key: 'mission_content', label: 'Mission Content', type: 'textarea', default: '' },
      { key: 'values', label: 'Values', type: 'array', fields: ['title', 'description'] },
    ]
  }
}

export default function AdminPageEditor() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const { isAuthenticated, isAdmin } = useAuth()

  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [content, setContent] = useState({})
  const [existingId, setExistingId] = useState(null)

  const pageConfig = pageConfigs[slug]

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/account')
      return
    }
    if (!isAdmin) {
      navigate('/')
      return
    }
    if (!pageConfig) {
      navigate('/admin/pages')
      return
    }
    fetchPageContent()
  }, [isAuthenticated, isAdmin, slug])

  const fetchPageContent = async () => {
    try {
      const { data, error } = await supabase
        .from('page_content')
        .select('*')
        .eq('page_slug', slug)
        .single()

      if (data) {
        setContent(data.content || {})
        setExistingId(data.id)
      } else {
        // Initialize with defaults
        const defaults = {}
        pageConfig.sections.forEach(section => {
          if (section.default !== undefined) {
            defaults[section.key] = section.default
          } else if (section.type === 'array') {
            defaults[section.key] = [{ title: '', description: '' }]
          } else {
            defaults[section.key] = ''
          }
        })
        setContent(defaults)
      }
    } catch (error) {
      // Table might not exist yet, use defaults
      const defaults = {}
      pageConfig.sections.forEach(section => {
        if (section.default !== undefined) {
          defaults[section.key] = section.default
        } else if (section.type === 'array') {
          defaults[section.key] = [{ title: '', description: '' }]
        } else {
          defaults[section.key] = ''
        }
      })
      setContent(defaults)
    } finally {
      setIsLoading(false)
    }
  }

  const handleChange = (key, value) => {
    setContent(prev => ({ ...prev, [key]: value }))
  }

  const handleArrayChange = (key, index, field, value) => {
    const newArray = [...(content[key] || [])]
    newArray[index] = { ...newArray[index], [field]: value }
    setContent(prev => ({ ...prev, [key]: newArray }))
  }

  const addArrayItem = (key) => {
    const newArray = [...(content[key] || []), { title: '', description: '' }]
    setContent(prev => ({ ...prev, [key]: newArray }))
  }

  const removeArrayItem = (key, index) => {
    const newArray = (content[key] || []).filter((_, i) => i !== index)
    setContent(prev => ({ ...prev, [key]: newArray.length ? newArray : [{ title: '', description: '' }] }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSaving(true)

    try {
      const pageData = {
        page_slug: slug,
        page_name: pageConfig.name,
        content: content,
        updated_at: new Date().toISOString()
      }

      if (existingId) {
        const { error } = await supabase
          .from('page_content')
          .update(pageData)
          .eq('id', existingId)

        if (error) throw error
      } else {
        const { data, error } = await supabase
          .from('page_content')
          .insert(pageData)
          .select()
          .single()

        if (error) throw error
        setExistingId(data.id)
      }

      alert('Page content saved successfully!')
    } catch (error) {
      console.error('Error saving page:', error)
      alert('Failed to save page content: ' + error.message)
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-pp-ivory flex items-center justify-center">
        <div className="animate-pulse text-pp-stone">Loading page content...</div>
      </div>
    )
  }

  if (!pageConfig) {
    return null
  }

  return (
    <div className="min-h-screen bg-pp-linen">
      <div className="container mx-auto px-4 lg:px-8 py-8">
        <Link
          to="/admin/pages"
          className="inline-flex items-center gap-2 text-pp-stone hover:text-pp-charcoal transition-colors mb-6"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Pages
        </Link>

        <h1 className="font-display text-display-md text-pp-ink mb-8">
          Edit {pageConfig.name}
        </h1>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {pageConfig.sections.map((section) => (
              <div key={section.key} className="bg-white rounded-2xl p-6 border border-pp-sand/50">
                <h2 className="font-display text-lg text-pp-ink mb-4">{section.label}</h2>

                {section.type === 'text' && (
                  <Input
                    value={content[section.key] || ''}
                    onChange={(e) => handleChange(section.key, e.target.value)}
                    placeholder={section.default || `Enter ${section.label.toLowerCase()}`}
                  />
                )}

                {section.type === 'textarea' && (
                  <textarea
                    value={content[section.key] || ''}
                    onChange={(e) => handleChange(section.key, e.target.value)}
                    rows={4}
                    className="w-full px-4 py-3 border border-pp-sand rounded-lg bg-white text-pp-ink focus:outline-none focus:ring-2 focus:ring-pp-terracotta/20 resize-none"
                    placeholder={`Enter ${section.label.toLowerCase()}`}
                  />
                )}

                {section.type === 'image' && (
                  <div className="space-y-4">
                    {content[section.key] ? (
                      <div className="relative inline-block">
                        <img
                          src={content[section.key]}
                          alt={section.label}
                          className="max-w-xs rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => handleChange(section.key, '')}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <ImageUpload
                        images={content[section.key] ? [content[section.key]] : ['']}
                        onChange={(images) => handleChange(section.key, images[0] || '')}
                        maxImages={1}
                      />
                    )}
                  </div>
                )}

                {section.type === 'array' && (
                  <div className="space-y-4">
                    {(content[section.key] || []).map((item, index) => (
                      <div key={index} className="p-4 bg-pp-linen/50 rounded-lg space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-pp-charcoal">Item {index + 1}</span>
                          <button
                            type="button"
                            onClick={() => removeArrayItem(section.key, index)}
                            className="p-1 text-pp-stone hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        {section.fields.map((field) => (
                          <div key={field}>
                            <label className="block text-sm text-pp-stone mb-1 capitalize">{field}</label>
                            {field === 'description' ? (
                              <textarea
                                value={item[field] || ''}
                                onChange={(e) => handleArrayChange(section.key, index, field, e.target.value)}
                                rows={2}
                                className="w-full px-3 py-2 border border-pp-sand rounded-lg text-sm"
                                placeholder={`Enter ${field}`}
                              />
                            ) : (
                              <input
                                type="text"
                                value={item[field] || ''}
                                onChange={(e) => handleArrayChange(section.key, index, field, e.target.value)}
                                className="w-full px-3 py-2 border border-pp-sand rounded-lg text-sm"
                                placeholder={`Enter ${field}`}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addArrayItem(section.key)}
                      className="inline-flex items-center gap-2 text-sm text-pp-terracotta hover:underline"
                    >
                      <Plus className="w-4 h-4" />
                      Add item
                    </button>
                  </div>
                )}
              </div>
            ))}

            <Button type="submit" className="w-full" isLoading={isSaving}>
              <Save className="w-4 h-4" />
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
